import {
  Container,
  Box,
  Stack,
  TextField,
  Typography,
  Button,
  Grid2
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { isNil } from 'lodash/fp'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Auth0Context, useAuth0 } from "@auth0/auth0-react"
import { AppEntrySubscription } from '@mzc-watchover/terashite-entry-graphql'
import log from 'loglevel'

import {
  RegistrationStatus,
  UserOutletContext
} from '../../types'
import {
  domainName,
  httpRequest,
  updateSubscription
} from '../../functions/common'
import { Loading } from '../Loading'
import { ServerError } from '../ServerError'
import {
  mergeSubscriptionDataFromForm
} from './functions'
import { CommonAddressForm } from '../CommonAddressForm'

log.setLevel(log.levels.DEBUG)

export const ContractEdit = () => {
  const { user } = useAuth0(Auth0Context)
  const updateSchema = {
    lastName: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[^ -~｡-ﾟ]+$/, '全角で入力してください'),
    firstName: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[^ -~｡-ﾟ]+$/, '全角で入力してください'),
    lastNameKana: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[ぁ-ゞー]+$/, 'ひらがなで入力してください'),
    firstNameKana: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[ぁ-ゞー]+$/, 'ひらがなで入力してください'),
    postalCode: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[0-9]{3}-[0-9]{4}$/, '999-9999のフォーマットで入力してください'),
    pref: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[^ -~｡-ﾟ]+$/, '全角で入力してください'),
    city: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[^ -~｡-ﾟ]+$/, '全角で入力してください'),
    town: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[^ -~｡-ﾟ]+$/, '全角で入力してください'),
    place: z
      .string()
      .regex(/^[^ -~｡-ﾟ]+$/, '全角で入力してください')
      .or(z.string().length(0)),
    phone: z
      .string()
      .min(12, { message: '数字は10桁または11桁で入力してください' })
      .max(13, { message: '数字は10桁または11桁で入力してください' })
      .regex(/^[0-9,-]+$/, '半角数字または半角ハイフンで入力してください'),
  }
  let schema = z.object(updateSchema)
  const useFormMethods = useForm({
    resolver: zodResolver(schema)
  })
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useFormMethods
  const navigate = useNavigate()
  const { memberId } = useOutletContext<UserOutletContext>()
  const [loading, setLoding] = useState<boolean>(true)
  const [notExistsUser, setNotExistsUser] = useState<boolean>(false)
  const [kind, setKind] = useState<RegistrationStatus | undefined>(undefined)
  const [subscription, setSubscription] = useState<
    AppEntrySubscription | undefined
  >(undefined)
  const [sub, setSub] = useState<string | undefined>(undefined)

  useEffect(() => {
    const loadCustomerData = async () => {
      const customerData = await httpRequest(
        'get',
        `https://${domainName}/v1/users/${memberId}`,
        undefined,
        { authorization: user?.sub }
      )
      if (isNil(customerData)) {
        log.error(
          '[CustomerInfo] cannot get customer data'
        )
        setNotExistsUser(true)
        return undefined
      }
      const { status, data } = customerData
      if (status !== 200) {
        log.error(
          '[CustomerInfo] cannot found customer data'
        )
        setNotExistsUser(true)
        return undefined
      }
      const { status: registStatus, subscription } = data
      setKind(registStatus)
      if (registStatus === RegistrationStatus.subscription) {
        setSubscription(subscription)
        const {
          lastName,
          firstName,
          lastNameHiragana,
          firstNameHiragana,
          zip,
          state,
          city,
          address,
          phoneNo
        } = subscription
        setValue('lastName', lastName)
        setValue('firstName', firstName)
        setValue('lastNameKana', lastNameHiragana)
        setValue('firstNameKana', firstNameHiragana)
        setValue('postalCode', zip)
        setPostalCode(zip)
        setValue('pref', state)
        setPref(state)
        setValue('city', city)
        setCity(city)
        // setValue('address', address)
        setTown(address)
        setValue('phone', phoneNo)
        setSub(user?.sub)
      } else {
        log.error(
          '[CustomerInfo] cannot found subscription data'
        )
        setNotExistsUser(true)
        return undefined
      }
      setLoding(false)
    }
    loadCustomerData()
  }, [])

  const [postalCode, setPostalCode] = useState<string>(
    !isNil(subscription)
      ? subscription.zip
      : ''
  )
  const [pref, setPref] = useState<string>(
    !isNil(subscription) ? subscription.state : ''
  )
  const [city, setCity] = useState<string>(
    !isNil(subscription) ? subscription.city : ''
  )
  const [town, setTown] = useState<string>(
    !isNil(subscription)
      ? subscription.address
      : ''
  )
  const [place, setPlace] = useState<string>('')
  // const [phone, setPhone] = useState<string>(
  //   !isNil(subscription)
  //     ? subscription.phoneNo
  //     : !isNil(entry)
  //     ? entry.phone
  //     : ''
  // )

  const onSubmit = (data: any) => {
    setLoding(true)
    const updateCustomer = async () => {
      if (isNil(subscription) || isNil(sub)) {
        setNotExistsUser(true)
        return undefined
      }
      if (kind === RegistrationStatus.subscription) {
        const result = await updateSubscription(
          mergeSubscriptionDataFromForm(
            subscription as AppEntrySubscription,
            data
          ),
          sub
        )
        if (isNil(result) || result === false) {
          log.error(
            '[CustomerEdit] faild to update subscription'
          )
        }
      } else {
        log.error(
          '[CustomerEdit] illegal kind data'
        )
      }
      navigate('/user/info')
    }
    updateCustomer()
  }
  return loading ? (
    <Loading />
  ) : notExistsUser ? (
    <ServerError />
  ) : (
    <FormProvider {...useFormMethods}>
      <Container sx={{ pt: 5 }}>
        <Box component="form" sx={{ mt: 1 }}>
          <Typography
            align="left"
            variant="h4"
            sx={{
              borderLeft: 20,
              borderBottom: 1,
              borderColor: 'orange',
              paddingLeft: 1
            }}>
            ご契約者さまの登録情報変更
          </Typography>
          <Typography variant="caption">
            変更する項目をすべてご入力いただき【登録】ボタンを押してください。
          </Typography>
          <Typography variant="caption" color="primary">
            * 箇所は入力必須項目です。
          </Typography>
          <Typography variant="subtitle1">
            <Typography component={'span'} color="red">
              *
            </Typography>
            <Typography component={'span'} sx={{ ml: 1 }}>
              お名前（漢字）
            </Typography>
          </Typography>
          <Stack spacing={3} direction="row" useFlexGap>
            <TextField
              id="lastName"
              {...register('lastName')}
              error={'lastName' in errors}
              helperText={errors.lastName?.message as string}
              label="姓"
              placeholder="中電"
              required
              variant="filled"
              fullWidth
              margin="dense"></TextField>
            <TextField
              id="firstName"
              {...register('firstName')}
              error={'firstName' in errors}
              helperText={errors.firstName?.message as string}
              label="名"
              placeholder="太郎"
              required
              variant="filled"
              fullWidth
              margin="dense"></TextField>
          </Stack>
          <Typography variant="subtitle1">
            <Typography component={'span'} color="red">
              *
            </Typography>
            <Typography component={'span'} sx={{ ml: 1 }}>
              お名前（かな）
            </Typography>
          </Typography>
          <Stack spacing={3} direction="row" useFlexGap>
            <TextField
              id="lastNameKana"
              {...register('lastNameKana')}
              error={'lastNameKana' in errors}
              helperText={errors.lastNameKana?.message as string}
              // defaultValue={lastNameKana}
              label="せい"
              placeholder="ちゅうでん"
              required
              variant="filled"
              fullWidth
              margin="dense"></TextField>
            <TextField
              id="firstNameKana"
              {...register('firstNameKana')}
              error={'firstNameKana' in errors}
              helperText={errors.firstNameKana?.message as string}
              // defaultValue={firstNameKana}
              label="めい"
              placeholder="たろう"
              required
              variant="filled"
              fullWidth
              margin="dense"></TextField>
          </Stack>
          <CommonAddressForm
            label="住所自動入力"
            postalCode={postalCode}
            forms={{
              setters: {
                setPostalCode,
                setPref,
                setCity,
                setTown,
                setPlace
              },
              formDatas: {
                postalCode,
                pref,
                city,
                town,
                place
              }
            }}
          />
          <Typography variant="subtitle1">
            <Typography component={'span'} color="red">
              *
            </Typography>
            <Typography component={'span'} sx={{ ml: 1 }}>
              電話番号（半角）
            </Typography>
          </Typography>
          <Stack spacing={3} direction="row">
            <TextField
              id="phone"
              {...register('phone')}
              error={'phone' in errors}
              helperText={errors.phone?.message as string}
              // defaultValue={phone}
              label="電話番号（半角）"
              placeholder="例）090-1234-5678"
              required
              variant="filled"
              size={'medium'}></TextField>
          </Stack>
        </Box>
      </Container>
      <Grid2>
        <Box display={'flex'} justifyContent={'space-around'} paddingTop={3}>
          <Box justifyContent={'start'}>
            <Button variant="outlined" onClick={() => navigate('/user/info')}>
              戻る
            </Button>
          </Box>
          <Box justifyContent={'end'}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit(onSubmit)}>
              登録
            </Button>
          </Box>
        </Box>
      </Grid2>
    </FormProvider>
  )
}
